import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminService, { type AdminDeveloperRow } from '../../../services/adminService';

export default function AdminDevelopersPage() {
  const [rows, setRows] = useState<AdminDeveloperRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminService.getDevelopers({
      search: search || undefined,
      status_filter: statusFilter || undefined,
      verified_filter: verifiedFilter || undefined,
      limit: 50,
    })
      .then((res) => { setRows(res.developers); setTotal(res.total); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [search, statusFilter, verifiedFilter]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Link to="/admin" className="text-sm text-[#003930] hover:underline">← Admin</Link>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">Developers <span className="text-base font-normal text-gray-400">({total})</span></h1>
          </div>
          <Link to="/admin/investor-leads" className="rounded-lg bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Investor leads →</Link>
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email…" className="flex-1 min-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All verification</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending FAL</option>
          </select>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {loading ? (
            <p className="p-8 text-center text-sm text-gray-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-400">No developers found.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Developer</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Projects</th>
                  <th className="px-4 py-3">Units</th>
                  <th className="px-4 py-3">Team</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {d.logo_url ? <img src={d.logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" /> : <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--pv-color-surface-sunken)] text-[#003930]">{d.name.charAt(0)}</div>}
                        <div>
                          <div className="font-medium text-gray-900">{d.name}</div>
                          <div className="text-xs text-gray-400">{d.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{d.headquarters_city || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{d.project_count}</td>
                    <td className="px-4 py-3 text-gray-600">{d.listing_count}</td>
                    <td className="px-4 py-3 text-gray-600">{d.team_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-[11px] ${d.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{d.is_active ? 'Active' : 'Inactive'}</span>
                        <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-[11px] ${d.is_verified ? 'bg-[#DDEEEB] text-[#003930]' : 'bg-amber-100 text-amber-700'}`}>{d.is_verified ? 'Verified' : 'Pending FAL'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/developers/${d.id}`} className="rounded-lg bg-[#004D40] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#003930]">Manage</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
